import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IPrNewsSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import { Page } from 'src/components/Page';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { INewsGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { newsApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { NewsFormDialog } from './NewsFormDialog';

/**
 * ## NewsGrid
 * 뉴스 관리 페이지
 */
interface INewsGridProps {
  searchData: Partial<IPrNewsSFilter>;
}

export const NewsGrid = ({ searchData }: INewsGridProps) => {
  const { getNewsGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { t } = useTranslation();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<INewsGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 목록 조회
  const getNewsData = useCallback(async () => {
    const pageParams = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    await newsApi.getNews(pageParams).then((res) => {
      if (res) {
        const newGridRows = res.data.map((news, i) => {
          return { ...news, id: news.newsSid ?? i };
        });
        setGridRows(newGridRows);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [searchData, pagination.page, pagination.size]);

  useEffect(() => {
    getNewsData();
  }, [getNewsData]);

  // 등록 버튼: 등록 dialog open
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.NEWS,
      formType: FormType.CREATE,
      maxWidth: 'xl',
      dialogContent: (
        <NewsFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<INewsGridRow>) => {
    callApiWithLoading(async () => {
      await newsApi.getDetail(param.row.newsSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.NEWS,
            formType: FormType.UPDATE,
            maxWidth: 'xl',
            dialogContent: (
              <NewsFormDialog
                formType={FormType.UPDATE}
                selectedData={res.data}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.NEWS);
    getNewsData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    const newPagination = {
      ...pagination,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    };
    setPagination(newPagination);
  };

  return (
    <Page>
      <ContentBox>
        <ContentBoxButtons>
          <ContentButtonCreate onClick={handleAdd}>
            {t('common.button.register')}
          </ContentButtonCreate>
        </ContentBoxButtons>
        <CDataGridPro
          columns={getNewsGridCol()}
          rows={gridRows}
          rowCount={toJS(pagination.totalElements)}
          paginationModel={{
            page: pagination.page - 1,
            pageSize: pagination.size,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          onRowDoubleClick={handleRowDoubleClick}
          slots={{ footer: CGridFooter }}
        />
      </ContentBox>
    </Page>
  );
};
export default observer(NewsGrid);
